import { useEffect, useRef } from "react";

/**
 * Animated ground for the sign-in brand panel: a near-black surface, a slowly flickering light leak in the top-right
 * corner, and soft blue smoke drifting upward. Plain Canvas 2D at half resolution (no WebGPU, no dependency); it
 * draws a single still frame under prefers-reduced-motion and pauses while hidden or off-screen. The layout never
 * depends on it: the panel's solid background stays legible if the canvas cannot start.
 */
interface Blob {
  x: number;
  y: number;
  r: number;
  vy: number;
  sway: number;
  phase: number;
  freq: number;
  hue: number;
  alpha: number;
}

const SCALE = 0.5;
const GROUND = "#06060f";
/** Vetra accents: sky, blue, indigo. */
const STOPS: readonly [number, number, number][] = [
  [56, 189, 248],
  [37, 99, 235],
  [79, 70, 229],
];

function rgba(color: readonly [number, number, number], alpha: number): string {
  return `rgba(${String(color[0])}, ${String(color[1])}, ${String(color[2])}, ${alpha.toFixed(3)})`;
}

function mix(t: number): [number, number, number] {
  const clamped = Math.min(Math.max(t, 0), 1) * (STOPS.length - 1);
  const i = Math.min(Math.floor(clamped), STOPS.length - 2);
  const f = clamped - i;
  const a = STOPS[i] as [number, number, number];
  const b = STOPS[i + 1] as [number, number, number];
  return [
    Math.round(a[0] + (b[0] - a[0]) * f),
    Math.round(a[1] + (b[1] - a[1]) * f),
    Math.round(a[2] + (b[2] - a[2]) * f),
  ];
}

export function SmokeCanvas() {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let width = 0;
    let height = 0;
    let frame = 0;
    let last = performance.now();
    let visible = !document.hidden;
    let onScreen = true;
    let animating = false;
    const blobs: Blob[] = [];

    const make = (startY?: number): Blob => ({
      x: (0.1 + Math.random() * 0.8) * width,
      y: startY ?? height + Math.random() * height * 0.4,
      r: (0.16 + Math.random() * 0.16) * Math.max(width, height * 0.7),
      vy: 10 + Math.random() * 16,
      sway: 16 + Math.random() * 36,
      phase: Math.random() * Math.PI * 2,
      freq: 0.12 + Math.random() * 0.18,
      hue: Math.random(),
      alpha: 0.08 + Math.random() * 0.09,
    });
    const resize = (): void => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = Math.max(1, Math.round(width * SCALE));
      canvas.height = Math.max(1, Math.round(height * SCALE));
      if (blobs.length === 0)
        for (let i = 0; i < 22; i += 1) blobs.push(make(Math.random() * height * 1.3));
    };
    const draw = (now: number): void => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      const t = now / 1000;
      ctx.setTransform(SCALE, 0, 0, SCALE, 0, 0);
      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = GROUND;
      ctx.fillRect(0, 0, width, height);
      const flicker = 0.5 + 0.5 * Math.sin(t * 1.6) * Math.sin(t * 3.7 + 1);
      const leak = ctx.createRadialGradient(
        width * 1.05,
        -height * 0.05,
        0,
        width * 1.05,
        -height * 0.05,
        Math.max(width, height) * 0.8,
      );
      leak.addColorStop(0, rgba(STOPS[0] as [number, number, number], 0.05 + 0.05 * flicker));
      leak.addColorStop(1, rgba(STOPS[0] as [number, number, number], 0));
      ctx.fillStyle = leak;
      ctx.fillRect(0, 0, width, height);
      ctx.globalCompositeOperation = "lighter";
      for (const blob of blobs) {
        blob.y -= blob.vy * dt;
        const x = blob.x + Math.sin(t * blob.freq + blob.phase) * blob.sway;
        const progress = 1 - (blob.y + blob.r) / (height + 2 * blob.r);
        const fade = Math.sin(Math.PI * Math.min(Math.max(progress, 0), 1));
        const alpha = blob.alpha * fade;
        const color = mix((progress * 0.7 + blob.hue * 0.5) % 1);
        const grad = ctx.createRadialGradient(x, blob.y, 0, x, blob.y, blob.r);
        grad.addColorStop(0, rgba(color, alpha));
        grad.addColorStop(0.55, rgba(color, alpha * 0.35));
        grad.addColorStop(1, rgba(color, 0));
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(x, blob.y, blob.r, 0, Math.PI * 2);
        ctx.fill();
        if (blob.y + blob.r < 0) Object.assign(blob, make());
      }
      if (animating) frame = requestAnimationFrame(draw);
    };
    const sync = (): void => {
      const shouldAnimate = visible && onScreen && !reduceMotion;
      if (shouldAnimate && !animating) {
        animating = true;
        last = performance.now();
        frame = requestAnimationFrame(draw);
      } else if (!shouldAnimate && animating) {
        animating = false;
        cancelAnimationFrame(frame);
      }
    };
    const onVisibility = (): void => {
      visible = !document.hidden;
      sync();
    };

    resize();
    draw(performance.now());
    sync();
    document.addEventListener("visibilitychange", onVisibility);
    const resizeObserver =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(resize);
    resizeObserver?.observe(canvas);
    const intersection =
      typeof IntersectionObserver === "undefined"
        ? null
        : new IntersectionObserver((entries) => {
            onScreen = entries.some((entry) => entry.isIntersecting);
            sync();
          });
    intersection?.observe(canvas);
    return () => {
      animating = false;
      cancelAnimationFrame(frame);
      document.removeEventListener("visibilitychange", onVisibility);
      resizeObserver?.disconnect();
      intersection?.disconnect();
    };
  }, []);
  return <canvas ref={ref} className="how-signin-canvas" aria-hidden="true" />;
}
